/**
 * Errors raised while a device is offline are kept on it and sent on reconnect (Nick, 2026-10-12:
 * "ensure offline errors get sent on reconnect"). Before this, every Sentry.init used the plain fetch
 * transport, so an error thrown with no signal was dropped and the field never told us about it.
 *
 * ONLY ERROR EVENTS ARE QUEUED. The SDK's offline queue writes EVERY replay envelope to IndexedDB, even
 * online (@sentry/core transports/offline.js `send`), and would hold session pings too. That would put
 * replays and session user ids at rest on the device and let them fill the slots an error needs. So
 * envelopes are routed: one carrying an `event` item goes through the queue; everything else (replays,
 * sessions, transactions, client reports) takes the plain fetch path exactly as before this change.
 *
 * WHY IT IS SAFE TO STORE THE ERRORS. The SDK runs `beforeSend` (our production-only gate and URL
 * scrubber) BEFORE it hands the event to a transport: @sentry/core client `_processEvent` ->
 * `processBeforeSend` -> `sendEvent` -> `transport.send`, and the queue writes to IndexedDB only inside
 * `transport.send`. So no error is stored that the scrubbers have not already seen, and nothing the gate
 * drops is stored at all. `__tests__/offlineTransport.test.ts` proves that order against the real SDK
 * client and the real IndexedDB queue, with a negative control, and proves replays/sessions are not stored.
 *
 * BOUNDS. At most OFFLINE_QUEUE_MAX error envelopes wait on the device (the SDK silently refuses new ones
 * past it), and an event older than OFFLINE_MAX_AGE_S when it is finally sent is dropped rather than
 * reported days late. The queue is flushed when the browser fires `online` and when the app next starts.
 *
 * NO SDK IMPORT. Each app passes ITS OWN SDK's makeFetchTransport and makeBrowserOfflineTransport, so
 * the types come from the caller's copy (client and client-tech install different @sentry/react versions,
 * and a transport typed from one is not assignable to the other's Sentry.init).
 *
 * TAG. `offline_delay_s` = whole seconds between capture (the event's own timestamp) and the send
 * attempt: about 0 for a live send, the time spent waiting for signal for a queued one.
 */
/** The SDK envelope shape this module reads: [headers, [itemHeader, payload][]]. Kept structural on purpose. */
type EnvelopeLike = readonly [unknown, ReadonlyArray<readonly [{ readonly type?: unknown }, unknown]>];

/** The two calls of a Sentry transport this module uses; typed by the caller's SDK copy. */
export interface TransportLike<E> {
  send(envelope: E): PromiseLike<unknown>;
  flush(timeout?: number): PromiseLike<boolean>;
}

/** Options the SDK's browser offline queue reads beyond the plain transport's own. */
export interface OfflineQueueOptions {
  maxQueueSize?: number;
  flushAtStartup?: boolean;
}

export const OFFLINE_QUEUE_MAX = 30;
export const OFFLINE_MAX_AGE_S = 72 * 60 * 60;
export const OFFLINE_DELAY_TAG = 'offline_delay_s';

interface StampableEvent {
  timestamp?: unknown;
  tags?: Record<string, unknown>;
}

function isEventPayload(payload: unknown): payload is StampableEvent {
  return typeof payload === 'object' && payload !== null && !(payload instanceof Uint8Array);
}

/**
 * A copy of the envelope with `offline_delay_s` on its event, or null when the event is too old to send.
 * Never mutates the input. Items that are not events (sessions, attachments, client reports) pass as-is.
 */
export function stampEnvelopeForSend<E>(envelope: E, nowS: number): E | null {
  const [headers, items] = envelope as unknown as EnvelopeLike;
  let expired = false;
  const stamped = items.map((item) => {
    const [itemHeader, payload] = item;
    if (itemHeader.type !== 'event' || !isEventPayload(payload) || typeof payload.timestamp !== 'number') {
      return item;
    }
    const delayS = Math.max(0, Math.round(nowS - payload.timestamp));
    if (delayS > OFFLINE_MAX_AGE_S) {
      expired = true;
      return item;
    }
    return [itemHeader, { ...payload, tags: { ...payload.tags, [OFFLINE_DELAY_TAG]: delayS } }] as const;
  });
  return expired ? null : ([headers, stamped] as unknown as E);
}

/** Wraps a transport factory so every send is stamped (and stale events dropped) at send time. */
export function withSendTimeStamp<O, E, T extends TransportLike<E>>(
  makeTransport: (options: O) => T,
  now: () => number = Date.now,
): (options: O) => T {
  return (options) => {
    const inner = makeTransport(options);
    const send = (envelope: E): PromiseLike<unknown> => {
      const stamped = stampEnvelopeForSend(envelope, now() / 1000);
      // Too old to be useful: answer as delivered so the offline queue lets it go.
      return stamped ? inner.send(stamped) : Promise.resolve({});
    };
    return { ...inner, send } as T;
  };
}

/** True when the envelope carries an error event: the only kind the offline queue may hold. */
export function isQueueableEnvelope(envelope: unknown): boolean {
  return (envelope as EnvelopeLike)[1].some(([itemHeader]) => itemHeader.type === 'event');
}

/**
 * The transport every Sentry.init passes, built from the CALLER's SDK:
 *   transport: makeOfflineAwareTransport(Sentry.makeFetchTransport, Sentry.makeBrowserOfflineTransport)
 * Error envelopes go through the SDK's IndexedDB offline queue (this file's bounds baked in:
 * BrowserTransportOptions has no field for them); every other envelope goes straight to the stamped fetch
 * transport, as before this change.
 */
export function makeOfflineAwareTransport<O, E, T extends TransportLike<E>>(
  makeTransport: (options: O) => T,
  makeOfflineTransport: (createTransport: (options: O) => T) => (options: O & OfflineQueueOptions) => T,
  now: () => number = Date.now,
): (options: O) => T {
  const stamped = withSendTimeStamp<O, E, T>(makeTransport, now);
  const offlineFactory = makeOfflineTransport(stamped);
  return (options) => {
    const direct = stamped(options);
    const offline = offlineFactory({ ...options, maxQueueSize: OFFLINE_QUEUE_MAX, flushAtStartup: true });
    const send = (envelope: E): PromiseLike<unknown> =>
      isQueueableEnvelope(envelope) ? offline.send(envelope) : direct.send(envelope);
    const flush = (timeout?: number): PromiseLike<boolean> =>
      Promise.all([offline.flush(timeout), direct.flush(timeout)]).then((done) => done.every(Boolean));
    return { ...direct, send, flush } as T;
  };
}
